import React from "react";
import { Link } from "react-router-dom";
import { Box } from "@mui/material";
import { GridCellParams } from "@mui/x-data-grid";
import EquipmentRowName from "components/EquipmentRowName";
import Tooltiper from "components/Tooltiper";
import DateViewer from "components/DateViewer";
import { EquipmentRelocationRequestEntity, TechnicalCondition } from "graphql/graphQlApiHooks";
import { TagStatusRelocationRequest } from "./TagStatusRelocationRequest";
import { EquipmentRelocationRequestStatusEnum } from "store/stores/equipment-relocation-request-status.enum";
import HistoryButton from "./HistoryButton";
import ActionButtons from "../ActionButtons";

interface EquipmentCellProps {
  row: EquipmentRelocationRequestEntity;
}

export const EquipmentCell = React.memo(function EquipmentCell({ row }: EquipmentCellProps) {
  const {
    equipment: {
      name,
      inventoryNumber,
      technicalCondition,
      photos,
      mainPhoto,
      inUse,
      failureReason,
      isEditable,
      id
    }
  } = row;

  const avatarUrl = mainPhoto ? mainPhoto.avatarUrl : (photos && photos?.length > 0) ? photos?.[0].avatarUrl : undefined;
  const avatarUrlOrigin = mainPhoto ? mainPhoto.url : (photos && photos?.length > 0) ? photos?.[0].url : undefined;

  return (
    <Link to={`/relocateEquipments/equipments/${id}`}>
      <EquipmentRowName
        name={name}
        inventoryNumber={inventoryNumber || "б/н"}
        inUse={inUse}
        technicalCondition={(technicalCondition === TechnicalCondition.Working)}
        avatarUrl={avatarUrl}
        avatarUrlOrigin={avatarUrlOrigin}
        failureReason={failureReason}
        isEditable={isEditable}
      />
    </Link>
  );
}, (prevProps, nextProps) => {
  const prev = prevProps.row.equipment;
  const next = nextProps.row.equipment;
  
  return (
    prev.id === next.id &&
    prev.name === next.name &&
    prev.inventoryNumber === next.inventoryNumber &&
    prev.technicalCondition === next.technicalCondition &&
    prev.inUse === next.inUse &&
    prev.failureReason === next.failureReason &&
    prev.isEditable === next.isEditable &&
    prev.mainPhoto?.id === next.mainPhoto?.id &&
    prev.mainPhoto?.avatarUrl === next.mainPhoto?.avatarUrl &&
    prev.mainPhoto?.url === next.mainPhoto?.url &&
    prev.photos?.length === next.photos?.length &&
    (prev.photos?.length === 0 || prev.photos?.[0]?.id === next.photos?.[0]?.id)
  );
});

interface SportObjectCellProps {
  row: EquipmentRelocationRequestEntity;
  field: 'sourceSportObject' | 'targetSportObject';
  emptyText: string;
}

export const SportObjectCell = React.memo(function SportObjectCell({ row, field, emptyText }: SportObjectCellProps) {
  const sportObject = row[field];

  return (
    <Tooltiper
      text={sportObject?.name ?
        <b>{sportObject?.name}</b> : emptyText
      }
      props={{
        variant: !sportObject?.name ? "body2" : "subtitle1",
        sx: {
          textAlign: "center"
        }
      }}
    />
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.row.id === nextProps.row.id &&
    prevProps.row[prevProps.field]?.id === nextProps.row[nextProps.field]?.id &&
    prevProps.row[prevProps.field]?.name === nextProps.row[nextProps.field]?.name
  );
});

interface StatusCellProps {
  row: EquipmentRelocationRequestEntity;
}

export const StatusCell = React.memo(function StatusCell({ row }: StatusCellProps) {
  return (
    <TagStatusRelocationRequest
      status={row.status as EquipmentRelocationRequestStatusEnum}
    />
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.row.id === nextProps.row.id &&
    prevProps.row.status === nextProps.row.status
  );
});

interface HistoryCellProps {
  params: GridCellParams;
}

export const HistoryCell = React.memo(function HistoryCell({ params }: HistoryCellProps) {
  return (
    <HistoryButton
      params={params}
    />
  );
}, (prevProps, nextProps) => {
  const prev = prevProps.params.row;
  const next = nextProps.params.row;
  return (
      prev.id === next.id &&
      prev.acceptBySportObjectComments === next.acceptBySportObjectComments &&
      prev.acceptComments === next.acceptComments &&
      prev.rejectReason === next.rejectReason
  );
});

interface DateCellProps {
  row: EquipmentRelocationRequestEntity;
}

export const DateCell = React.memo(function DateCell({ row }: DateCellProps) {
  return (
    <Box>
      <DateViewer date={row.createdAt} />
    </Box>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.row.id === nextProps.row.id &&
    prevProps.row.createdAt === nextProps.row.createdAt
  );
});

interface ActionsCellProps {
  row: EquipmentRelocationRequestEntity;
}

export const ActionsCell = React.memo(function ActionsCell({ row }: ActionsCellProps) {
  return (
    <ActionButtons
      relocateEquipment={row}
    />
  );
}, (prevProps, nextProps) => {
  return (
      prevProps.row.id === nextProps.row.id &&
      prevProps.row.status === nextProps.row.status
  );
});

